import { CommonModule } from '@angular/common';
import { Component, Input, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { SurveyPage, SurveySectionAnswers } from '../../models/survey.models';
import { SurveyStarRatingComponent } from '../survey-star-rating/survey-star-rating.component';

@Component({
  selector: 'app-survey-public-review-preview',
  standalone: true,
  imports: [CommonModule, MatButtonModule, SurveyStarRatingComponent],
  templateUrl: './survey-public-review-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './survey-public-review-preview.component.scss',
})
export class SurveyPublicReviewPreviewComponent {
  @Input() form!: FormGroup;
  @Input() pages!: SurveyPage[];
  @Input() locale: 'fi' | 'en' = 'fi';
  @Input() firstName: string | null = null;

  expanded = signal(false);

  // Real first name when available, otherwise the localized placeholder.
  protected get displayName(): string {
    const name = this.firstName?.trim();
    return name ? name : this.uiText('title');
  }

  // We extract the answers from the form defensively
  protected getAnswersForSection(sectionId: string): SurveySectionAnswers {
    const control = this.form.controls[sectionId];
    return control ? control.value || {} : {};
  }

  protected get tripGeneralAnswers() {
    return this.getAnswersForSection('trip_general');
  }

  protected get generalRating() {
    return this.tripGeneralAnswers['trip_general_rating'] as number | null;
  }

  protected get easeRating() {
    return this.tripGeneralAnswers['trip_general_arrangements_ease_rating'] as number | null;
  }

  protected get valueForMoneyRating() {
    return this.tripGeneralAnswers['trip_general_value_for_money_rating'] as number | null;
  }

  protected get generalFeedback() {
    return this.tripGeneralAnswers['trip_general_feedback'] as string | null;
  }

  protected get tripSubtitle(): string {
    const page = this.pages.find((p) => p.id === 'tripReview');
    const generalSection = page?.sections.find((s) => s.id === 'trip_general');
    const context = generalSection?.tripPhaseContext?.[0];
    const name = context?.name || 'Matka';

    // Add current month/year to match {päivämäärä} conceptually since there's no actual date in survey data
    const dateStr = new Date().toLocaleDateString(this.locale === 'fi' ? 'fi-FI' : 'en-US', {
      month: 'long',
      year: 'numeric',
    });

    return `${name}, ${dateStr}`;
  }

  protected get stars() {
    return [1, 2, 3, 4, 5];
  }

  protected get otherRatings(): { name: string; rating: number }[] {
    const ratings: { name: string; rating: number }[] = [];
    const page = this.pages.find((p) => p.id === 'tripReview');
    if (!page) return ratings;

    for (const section of page.sections) {
      if (section.id === 'trip_general') continue;

      // Find the specific section control key.
      const sectionKey = section.tripPhaseId
        ? `${section.id}.tp${section.tripPhaseId}`
        : section.id;
      const answers = this.getAnswersForSection(sectionKey);

      const ratingKeys = Object.keys(answers).filter((k) => k.endsWith('rating'));

      for (const key of ratingKeys) {
        const rating = answers[key] as number | null;
        if (rating) {
          // We simplify the name by grabbing the section title + maybe prefix
          let nameStr =
            typeof section.title === 'string'
              ? section.title
              : section.title[this.locale] || section.title['fi'];

          if (section.tripPhaseContext?.[0]?.name) {
            nameStr = section.tripPhaseContext[0].name;
          }

          if (key.includes('breakfast')) {
            nameStr += this.locale === 'en' ? ' Breakfast' : ' Aamiainen';
          } else if (key.includes('spa')) {
            nameStr += this.locale === 'en' ? ' Spa' : ' Kylpylä';
          }

          ratings.push({ name: nameStr, rating });
        }
      }
    }
    return ratings;
  }

  // Previews only the first 2 other ratings when not expanded
  protected get previewOtherRatings() {
    return this.expanded() ? this.otherRatings : this.otherRatings.slice(0, 2);
  }

  protected get phaseReviews() {
    const reviews: {
      name: string;
      ratings: { name: string; rating: number }[];
      comment?: string;
    }[] = [];
    const page = this.pages.find((p) => p.id === 'tripReview');
    if (!page) return reviews;

    for (const section of page.sections) {
      if (section.id === 'trip_general') continue;

      const sectionKey = section.tripPhaseId
        ? `${section.id}.tp${section.tripPhaseId}`
        : section.id;
      const answers = this.getAnswersForSection(sectionKey);

      const sectionRatings: { name: string; rating: number }[] = [];
      const ratingKeys = Object.keys(answers).filter((k) => k.endsWith('rating'));

      for (const key of ratingKeys) {
        const rating = answers[key] as number | null;
        if (rating) {
          let subName = '';
          if (key.includes('breakfast')) {
            subName = this.locale === 'en' ? 'Breakfast' : 'Aamiainen';
          } else if (key.includes('spa')) {
            subName = this.locale === 'en' ? 'Spa' : 'Kylpylä';
          }
          sectionRatings.push({ name: subName, rating });
        }
      }

      let sectionComment = '';
      const feedbackKeys = Object.keys(answers).filter((k) => k.endsWith('feedback'));
      for (const key of feedbackKeys) {
        const comment = answers[key] as string | null;
        if (comment && comment.trim()) {
          sectionComment = comment;
          break;
        }
      }

      if (sectionRatings.length > 0 || sectionComment) {
        let nameStr =
          typeof section.title === 'string'
            ? section.title
            : section.title[this.locale] || section.title['fi'];
        if (section.tripPhaseContext?.[0]?.name) {
          nameStr = section.tripPhaseContext[0].name;
        }
        reviews.push({ name: nameStr, ratings: sectionRatings, comment: sectionComment });
      }
    }
    return reviews;
  }

  // Same for comments in other sections
  protected get otherComments(): { name: string; comment: string }[] {
    const comments: { name: string; comment: string }[] = [];
    const page = this.pages.find((p) => p.id === 'tripReview');
    if (!page) return comments;

    for (const section of page.sections) {
      if (section.id === 'trip_general') continue;

      const sectionKey = section.tripPhaseId
        ? `${section.id}.tp${section.tripPhaseId}`
        : section.id;
      const answers = this.getAnswersForSection(sectionKey);

      const feedbackKeys = Object.keys(answers).filter((k) => k.endsWith('feedback'));

      for (const key of feedbackKeys) {
        const comment = answers[key] as string | null;
        if (comment && comment.trim()) {
          let nameStr =
            typeof section.title === 'string'
              ? section.title
              : section.title[this.locale] || section.title['fi'];
          if (section.tripPhaseContext?.[0]?.name) {
            nameStr = section.tripPhaseContext[0].name;
          }
          comments.push({ name: nameStr, comment });
        }
      }
    }
    return comments;
  }

  toggleExpand() {
    this.expanded.update((v) => !v);
  }

  uiText(
    key:
      | 'title'
      | 'general'
      | 'showMore'
      | 'showLess'
      | 'heading'
      | 'subheading'
      | 'valueForMoney'
      | 'ease'
      | 'noComment',
  ) {
    // Keep labels that correspond to survey questions in sync with
    // apps/modern-backend/src/survey/survey.schema.ts and the demo schema in survey-page.component.ts.
    const texts = {
      fi: {
        title: 'Etunimesi',
        general: 'Yleinen',
        showMore: '+ Näytä koko arvostelu',
        showLess: '- Pienennä koko arvostelu',
        heading: 'Arvostelusi esikatselu',
        subheading: '🌍 Arvostelu julkaistaan etunimelläsi. Voit vielä muokata ennen lähetystä.',
        valueForMoney: 'Vastinetta rahoille',
        ease: 'Vaivattomuus',
        noComment: 'Ei kommenttia',
      },
      en: {
        title: 'Your first name',
        general: 'General',
        showMore: '+ Show full review',
        showLess: '- Show less',
        heading: 'Preview of your review',
        subheading:
          '🌍 Your review will be published under your first name. You can still edit it before sending.',
        valueForMoney: 'Value for money',
        ease: 'Ease',
        noComment: 'No comment',
      },
    };
    return texts[this.locale][key];
  }
}
